import React, { FC, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@mui/material/Typography';
import { RedeemModalProps } from '@src/types/common/modal.types';

import {
  useApprove,
  useBalance,
} from '@services/contract/common/erc1155.contract';

import { useAuth } from '@contexts/common/AuthorizationContext';
import { modalActions } from '@contexts/common/ModalContext';

import useCreateRedeemNft from '@hooks/bonds/useCreateRedeemNft';

import { modifyEvent } from '@helpers/common/form.helpers';

import { formatBNWithCommas, toBN } from '@utils/formaters.utils';

import { amountYup } from '@validators/vaults/common.validators';

import Button from '@components/common/Button';
import Input from '@components/common/Input';
import ModalWrapper from '@components/common/Modal/ModalWrapper';
import SkeletonLoader from '@components/common/SkeletonLoader';
import SwitchChainButton from '@components/common/SwitchChainButton';

import { REDEEM_PERCENT } from '@constants/bonds/common.constants';
import {
  NFT_BONDS,
  NFT_REDEMPTION,
} from '@constants/bonds/contracts.constants';
import {
  DEFAULT_CRITERIA_MODE,
  DEFAULT_REVALIDATE_MODE,
  FieldNames,
} from '@constants/common/form.constants';
import { ONLY_POSITIVE_INTEGERS } from '@constants/common/regex.constants';
import { Size } from '@constants/common/ui.constants';

import useStyles from './styles.module';

type FormValues = {
  [FieldNames.Amount]: string;
};

const RedeemModal: FC<RedeemModalProps> = ({
  chainId,
  handleUpdateInfo,
  bondPriceUsd,
  nativeToken,
  bondPrice,
}) => {
  const { classes } = useStyles();
  const { account } = useAuth();

  const {
    data: balance,
    isPending: isPendingBalance,
    refetch: refetchBalance,
  } = useBalance({
    nftAddress: NFT_BONDS,
    chainId,
  });

  const {
    isApproved,
    approve,
    isPendingApprove,
    refetch: refetchApprove,
  } = useApprove({
    spender: NFT_REDEMPTION,
    nftAddress: NFT_BONDS,
    chainId,
  });

  const handleUpdate = async () => {
    await Promise.all([refetchBalance(), refetchApprove(), handleUpdateInfo()]);
  };

  const { redeem, isPending: isPendingRedeem } =
    useCreateRedeemNft(handleUpdate);

  const validationSchema = yup.object().shape({
    amount: amountYup(toBN(balance)),
  });

  const {
    control,
    handleSubmit,
    trigger,
    setValue,
    formState: { isValid },
    watch,
    reset,
  } = useForm<FormValues>({
    mode: DEFAULT_CRITERIA_MODE,
    reValidateMode: DEFAULT_REVALIDATE_MODE,
    resolver: yupResolver(validationSchema),
    defaultValues: { [FieldNames.Amount]: '' },
  });

  const redeemValue = toBN(watch(FieldNames.Amount) || 0);

  const onSubmit = async (formData: FormValues) => {
    await trigger();
    await redeem(Number(formData[FieldNames.Amount]));
    reset();
    await refetchBalance();
    modalActions.hide();
  };

  const onMaxHandlerStake = useCallback(() => {
    if (!account || !balance || toBN(balance).isZero()) return;
    setValue(FieldNames.Amount, toBN(balance).toFixed(0), {
      shouldValidate: true,
    });
  }, [balance, account]);

  const isDisableButton = !balance || toBN(balance).isZero() || !isValid;

  return (
    <ModalWrapper title="Redeem bonds">
      <form className={classes.container} onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="body1">
          Redeem your bonds anytime at {REDEEM_PERCENT * 100}% of their value.
          Select one or multiple bonds in a single request. Redemptions are
          processed within 7 days.
        </Typography>
        <div className={classes.totalBlock}>
          <div className={classes.row}>
            <Typography
              variant="body2"
              color={(theme) => theme.palette.custom.text.accent}
            >
              Redemption price
            </Typography>
            <Typography variant="subtitle2">
              {formatBNWithCommas(
                bondPrice.times(REDEEM_PERCENT),
                4,
                nativeToken.decimals,
              )}{' '}
              {nativeToken.symbol} ($
              {formatBNWithCommas(bondPriceUsd.times(REDEEM_PERCENT), 2)})
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography
              variant="subtitle2"
              color={(theme) => theme.palette.custom.text.accent}
            >
              Total price
            </Typography>
            <Typography variant="subtitle2">
              {isValid ? (
                <>
                  {formatBNWithCommas(
                    bondPrice.times(REDEEM_PERCENT).times(redeemValue),
                    4,
                    nativeToken.decimals,
                  )}{' '}
                  {nativeToken.symbol} ($
                  {formatBNWithCommas(
                    bondPriceUsd.times(REDEEM_PERCENT).times(redeemValue),
                    2,
                  )}
                  )
                </>
              ) : (
                '-'
              )}
            </Typography>
          </div>
        </div>
        <div className={classes.inputContainer}>
          {isPendingBalance ? (
            <SkeletonLoader
              className={classes.skeleton}
              classNameWrap={classes.skeleton}
            />
          ) : (
            <Typography
              variant="caption1"
              color={(theme) => theme.palette.custom.text.accent}
            >
              {`Balance: ${formatBNWithCommas(balance, 0)}`}
            </Typography>
          )}
          <Controller
            control={control}
            name={FieldNames.Amount}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                value={value}
                onBlur={onBlur}
                onChange={(e) =>
                  onChange(modifyEvent(e, ONLY_POSITIVE_INTEGERS))
                }
                placeholder="0"
                onMax={onMaxHandlerStake}
                inputError={error?.message}
                disabled={!account}
              />
            )}
          />
        </div>
        <SwitchChainButton chainId={chainId} size={Size.Large}>
          {isApproved ? (
            <Button
              disabled={isDisableButton}
              buttonType="submit"
              isLoading={isPendingRedeem}
            >
              Redeem
            </Button>
          ) : (
            <Button onClick={approve} isLoading={isPendingApprove}>
              Approve
            </Button>
          )}
        </SwitchChainButton>
      </form>
    </ModalWrapper>
  );
};

export default RedeemModal;
